/**
 * Soniox Transcription Service
 *
 * Manages microphone capture via MediaRecorder and WebSocket connection
 * to a Flask backend for live transcription using Soniox.
 *
 * Features:
 * - Ultra-low latency audio streaming (250ms chunks)
 * - WebSocket lifecycle management with exponential-backoff reconnect
 * - Final and interim transcript handling
 * - Graceful cleanup
 *
 * Compatible with Vue 2 - use as a mixin or import methods directly
 */
/* eslint-disable */
import { getAuth } from "firebase/auth";

const WS_PATHS = {
  soniox: "/ws/transcribe-soniox",
  deepgram: "/ws/transcribe-deepgram",
};

function resolveBaseUrl() {
  const url =
    process.env.VUE_APP_STT_BASE_URL ||
    process.env.VUE_APP_DEEPGRAM_STT_BASE_URL ||
    "https://deepgram-stt-ovytxk4eva-el.a.run.app";
  return url.replace(/\/$/, "");
}

/**
 * Create a new transcription instance
 * @param {Object} options
 * @param {'soniox'|'deepgram'} [options.provider='soniox']
 */
export function createDeepgramTranscription(options = {}) {
  const provider = options.provider || "soniox";
  if (!WS_PATHS[provider]) {
    throw new Error(`Unknown STT provider: "${provider}". Must be one of: ${Object.keys(WS_PATHS).join(", ")}`);
  }
  const BASE_URL = resolveBaseUrl();
  const WS_ENDPOINT = (BASE_URL + WS_PATHS[provider])
    .replace(/^http:/, "ws:")
    .replace(/^https:/, "wss:");

  const MAX_RECONNECT_ATTEMPTS = 5;

  console.log(`[Soniox] provider=${provider} endpoint=${WS_ENDPOINT}`);
  // State
  const state = {
    isRecording: false,
    isConnecting: false,
    isConnected: false,
    finalTranscript: "",
    interimTranscript: "",
    error: null,
    audioLevel: 0,
    shouldReconnect: false,
  };

  // Internal references
  let mediaRecorder = null;
  let mediaStream = null;
  let websocket = null;
  let audioContext = null;
  let analyser = null;
  let audioLevelInterval = null;
  let reconnectAttempts = 0;
  let reconnectTimer = null;
  let sonioxReady = false;
  let frontendAudioBuffer = [];
  const AUDIO_BUFFER_MAX = 200; // ~20s at 100ms chunks; drop oldest when exceeded

  // Callbacks for state changes (to be set by Vue component)
  let onStateChange = null;

  /**
   * Set callback for state changes
   */
  function setStateChangeCallback(callback) {
    onStateChange = callback;
  }

  /**
   * Update state and notify listeners
   */
  function updateState(updates) {
    Object.assign(state, updates);
    if (onStateChange) {
      onStateChange({ ...state });
    }
  }

  /**
   * Get current state
   */
  function getState() {
    return { ...state };
  }

  /**
   * Request microphone access and setup MediaRecorder
   */
  async function setupMicrophone() {
    try {
      mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      console.log("[Soniox] Microphone access granted");

      // Setup audio level monitoring
      setupAudioLevelMonitor(mediaStream);

      // Determine the best supported MIME type for audio
      const mimeType = getSupportedMimeType();
      console.log("[Soniox] Using MIME type:", mimeType);

      mediaRecorder = new MediaRecorder(mediaStream, {
        mimeType,
        audioBitsPerSecond: 16000,
      });

      // Handle audio data chunks - sent every 250ms
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          sendAudioChunk(event.data);
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error("[Soniox] MediaRecorder error:", event.error);
        updateState({ error: "Microphone recording error" });
        stopRecording();
      };

      mediaRecorder.onstop = () => {
        console.log("[Soniox] MediaRecorder stopped");
      };

      return true;
    } catch (err) {
      console.error("[Soniox] Microphone setup failed:", err);

      // Release any resources that were acquired before the failure
      if (mediaStream) {
        mediaStream.getTracks().forEach((t) => t.stop());
        mediaStream = null;
      }
      if (audioContext && audioContext.state !== "closed") {
        audioContext.close().catch(() => {});
        audioContext = null;
        analyser = null;
      }
      if (audioLevelInterval) {
        clearInterval(audioLevelInterval);
        audioLevelInterval = null;
      }

      let errorMessage = `Microphone error: ${err.message}`;
      if (err.name === "NotAllowedError") {
        errorMessage =
          "Microphone access denied. Please allow microphone access and try again.";
      } else if (err.name === "NotFoundError") {
        errorMessage =
          "No microphone found. Please connect a microphone and try again.";
      }

      updateState({ error: errorMessage });
      return false;
    }
  }

  /**
   * Get the best supported MIME type for audio recording
   */
  function getSupportedMimeType() {
    const types = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/mp4",
      "audio/wav",
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return "audio/webm"; // fallback
  }

  /**
   * Setup audio level monitoring for visual feedback
   */
  function setupAudioLevelMonitor(stream) {
    try {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      analyser.fftSize = 256;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      audioLevelInterval = setInterval(() => {
        if (analyser && state.isRecording) {
          analyser.getByteFrequencyData(dataArray);
          const average =
            dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
          const level = Math.min(100, Math.round(average * 1.5));
          if (level !== state.audioLevel) {
            updateState({ audioLevel: level });
          }
        }
      }, 50);
    } catch (err) {
      console.warn("[Soniox] Audio level monitoring not available:", err);
    }
  }

  /**
   * Connect to WebSocket backend
   */
  function connect() {
    return new Promise((resolve, reject) => {
      console.log("[Soniox] Connecting to WebSocket...");
      updateState({ isConnecting: true, error: null, shouldReconnect: true });

      try {
        websocket = new WebSocket(WS_ENDPOINT);

        websocket.onopen = async () => {
          console.log("[Soniox] WebSocket connected, sending init...");
          try {
            const currentUser = getAuth().currentUser;
            if (!currentUser) throw new Error("No authenticated user");
            const idToken = await currentUser.getIdToken();
            websocket.send(JSON.stringify({ type: "init", idToken }));
          } catch (err) {
            console.error("[Soniox] Failed to get ID token:", err);
            websocket.close(1008, "Auth failed");
            reject(err);
            return;
          }
          reconnectAttempts = 0;
          if (reconnectTimer) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
          }
          updateState({ isConnecting: false, isConnected: true });
          resolve();
        };

        websocket.onmessage = (event) => {
          handleWebSocketMessage(event);
        };

        websocket.onerror = (event) => {
          console.error("[Soniox] WebSocket error:", event);
          updateState({
            isConnecting: false,
            isConnected: false,
            error: "WebSocket connection failed",
          });
          reject(new Error("WebSocket connection failed"));
        };

        websocket.onclose = (event) => {
          console.log("[Soniox] WebSocket closed:", event.code, event.reason);
          updateState({ isConnecting: false, isConnected: false });
          const noRetryCloseCodes = new Set([1000, 1008]);
          const shouldRetry =
            state.shouldReconnect &&
            !noRetryCloseCodes.has(event.code) &&
            reconnectAttempts < MAX_RECONNECT_ATTEMPTS;
          if (shouldRetry) {
            reconnectAttempts += 1;
            const backoffMs = Math.min(
              10000,
              1000 * Math.pow(2, reconnectAttempts - 1),
            );
            console.log(
              `[Soniox] Unexpected close, retrying in ${backoffMs}ms (attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})...`,
            );
            // Reset ready flag and clear stale buffer — reconnect starts a new Soniox session
            // which needs fresh WebM headers; mid-stream chunks from before the drop are invalid
            sonioxReady = false;
            frontendAudioBuffer = [];
            reconnectTimer = setTimeout(() => {
              if (state.shouldReconnect) {
                connect().catch(console.error);
              }
            }, backoffMs);
          } else if (
            state.shouldReconnect &&
            reconnectAttempts >= MAX_RECONNECT_ATTEMPTS
          ) {
            console.error("[Soniox] Max reconnect attempts reached, giving up");
            updateState({
              error: "Connection lost. Please stop and restart recording.",
              shouldReconnect: false,
            });
          }
        };

        // Timeout for connection
        const connectTimeoutId = setTimeout(() => {
          if (state.isConnecting) {
            console.warn("[Soniox] WebSocket connection timeout");
            if (websocket) websocket.close();
            updateState({
              isConnecting: false,
              error: "Connection timeout",
            });
            reject(new Error("Connection timeout"));
          }
        }, 5000);

        // Clear timeout once connected
        websocket.addEventListener(
          "open",
          () => clearTimeout(connectTimeoutId),
          { once: true },
        );
      } catch (err) {
        console.error("[Soniox] WebSocket connection error:", err);
        updateState({
          isConnecting: false,
          error: "Failed to connect to transcription service",
        });
        reject(err);
      }
    });
  }

  /**
   * Handle incoming WebSocket messages from the backend.
   * Supports multiple message shapes for forward compatibility.
   */
  function handleWebSocketMessage(event) {
    try {
      const data = JSON.parse(event.data);

      // Server signals it is ready to receive audio
      if (data.type === "ready") {
        console.log(
          `[Soniox] Ready — flushing ${frontendAudioBuffer.length} buffered chunks`,
        );
        sonioxReady = true;
        for (const chunk of frontendAudioBuffer) {
          if (websocket && websocket.readyState === WebSocket.OPEN) {
            try {
              websocket.send(chunk);
            } catch (e) {
              /* socket closed mid-flush */
            }
          }
        }
        frontendAudioBuffer = [];
        return;
      }

      // Extract transcript and is_final from various possible formats
      let transcript = null;
      let isFinal = false;

      // Format 1: Simple { text, is_final }
      if (data.text !== undefined) {
        transcript = data.text;
        isFinal = data.is_final === true;
      }
      // Format 2: { transcript, is_final }
      else if (data.transcript !== undefined) {
        transcript = data.transcript;
        isFinal = data.is_final === true;
      }
      // Format 3: { type: "Results", channel: { alternatives: [...] } }
      else if (data.type === "Results" && data.channel) {
        const alternatives = data.channel.alternatives;
        if (alternatives && alternatives.length > 0) {
          transcript = alternatives[0].transcript || "";
        }
        isFinal = data.is_final === true;
      }
      // Format 4: Nested in 'data' property
      else if (data.data) {
        const nested = data.data;
        if (nested.text !== undefined) {
          transcript = nested.text;
          isFinal = nested.is_final === true;
        } else if (nested.transcript !== undefined) {
          transcript = nested.transcript;
          isFinal = nested.is_final === true;
        }
      }

      // Update state if we got a transcript
      if (transcript !== null) {
        if (isFinal) {
          // The backend sends the WHOLE settled transcript on every is_final
          // message, so REPLACE (do not append) — appending would duplicate
          // every word. Guard against an empty value wiping the transcript.
          if (transcript) {
            updateState({
              finalTranscript: transcript,
              interimTranscript: "",
            });
          }
        } else {
          // Interim carries only the live, still-changing tail; replace it.
          updateState({ interimTranscript: transcript });
        }
      }

      // Handle error messages from backend — stop reconnect so we don't loop on persistent failures
      if (data.error) {
        console.error("[Soniox] Backend error:", data.error);
        const allowReconnect = data.reconnect === true;
        updateState({ error: data.error, shouldReconnect: allowReconnect });
      }
    } catch (err) {
      console.error(
        "[Soniox] Failed to parse WebSocket message:",
        err,
        event.data,
      );
    }
  }

  function sendAudioChunk(audioBlob) {
    if (!websocket || websocket.readyState !== WebSocket.OPEN) return;
    if (!sonioxReady) {
      if (frontendAudioBuffer.length >= AUDIO_BUFFER_MAX)
        frontendAudioBuffer.shift();
      frontendAudioBuffer.push(audioBlob);
      return;
    }
    try {
      websocket.send(audioBlob);
    } catch (err) {
      console.warn("[Soniox] Failed to send audio chunk:", err);
    }
  }

  /**
   * Start recording and transcription
   */
  async function startRecording() {
    if (state.isRecording || state.isConnecting) {
      console.warn("[Soniox] Already recording or connecting");
      return false;
    }

    updateState({
      error: null,
      finalTranscript: "",
      interimTranscript: "",
    });

    // Setup microphone first
    const micReady = await setupMicrophone();
    if (!micReady) {
      return false;
    }

    // Ensure connection is established before recording
    if (!websocket || websocket.readyState !== WebSocket.OPEN) {
      try {
        await connect();
      } catch (err) {
        console.warn("[Soniox] Connection for recording failed:", err.message);
        stopRecording();
        return false;
      }
    } else {
      // Reusing existing open socket — arm reconnect so a mid-session drop is retried
      updateState({ shouldReconnect: true });
    }

    // Start capturing immediately — chunks are buffered until Soniox sends 'ready'
    sonioxReady = false;
    frontendAudioBuffer = [];
    if (mediaRecorder && mediaRecorder.state === "inactive") {
      mediaRecorder.start(250);
      console.log(
        "[Soniox] MediaRecorder started immediately, buffering until ready",
      );
    }
    updateState({ isRecording: true });
    return true;
  }

  /**
   * Stop recording and transcription
   */
  function stopRecording() {
    console.log("[Soniox] Stopping recording...");
    sonioxReady = false;
    frontendAudioBuffer = [];
    updateState({ isRecording: false, shouldReconnect: false });
    reconnectAttempts = 0;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }

    // Stop MediaRecorder
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      try {
        mediaRecorder.stop();
      } catch (err) {
        console.warn("[Soniox] Error stopping MediaRecorder:", err);
      }
    }

    // Stop audio level monitoring
    if (audioLevelInterval) {
      clearInterval(audioLevelInterval);
      audioLevelInterval = null;
    }

    // Stop media tracks
    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => {
        track.stop();
        console.log("[Soniox] Media track stopped:", track.kind);
      });
      mediaStream = null;
    }

    // Close audio context
    if (audioContext && audioContext.state !== "closed") {
      audioContext.close().catch(console.warn);
      audioContext = null;
      analyser = null;
    }

    // WebSocket closed when recording stops
    if (websocket) {
      websocket.close(1000, "Recording stopped");
      websocket = null;
    }

    mediaRecorder = null;
    updateState({ isConnected: false, audioLevel: 0 });

    console.log("[Soniox] Recording stopped, cleanup complete");
  }

  /**
   * Full cleanup (close WebSocket)
   */
  function disconnect() {
    const hasActiveStates = state.isConnected || state.isConnecting;
    const hasActiveSocket =
      websocket &&
      (websocket.readyState === WebSocket.OPEN ||
        websocket.readyState === WebSocket.CONNECTING);

    if (!hasActiveSocket && !hasActiveStates) return;
    console.log("[Soniox] Disconnecting WebSocket...");

    // Send stop signal before stopRecording nulls the socket
    if (websocket && websocket.readyState === WebSocket.OPEN) {
      try {
        websocket.send(JSON.stringify({ type: "stop" }));
      } catch (err) {
        console.warn("[Soniox] Error sending stop signal:", err);
      }
    }

    stopRecording();

    updateState({
      isConnected: false,
      isConnecting: false,
      shouldReconnect: false,
    });
    reconnectAttempts = 0;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  }

  /**
   * Clear transcripts
   */
  function clearTranscripts() {
    updateState({
      finalTranscript: "",
      interimTranscript: "",
    });
  }

  return {
    provider,
    getState,
    setStateChangeCallback,
    startRecording,
    stopRecording,
    clearTranscripts,
    connect,
    disconnect,
  };
}

export default createDeepgramTranscription;
